import { motion } from 'framer-motion';
import React, { useState } from 'react';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import axios from 'axios';
import { StyleSheet, css } from 'aphrodite';

const validationSchema = Yup.object().shape({
  hospitalAccreditations: Yup.array().of(Yup.object().shape({
    documentName: Yup.string()
      .trim()
      .required('Document name is required'),
    file: Yup.mixed()
      .required('File is required')
      .test('fileFormat', 'Unsupported file format', value => {
        if (!value) return false;
        const supportedFormats = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'];
        return supportedFormats.includes(value.type);
      })
      .test('fileSize', 'File size must be less than 5MB', value => {
        if (!value) return false;
        return value.size <= 5 * 1024 * 1024; // 5MB
      }),
    issueDate: Yup.date()
      .required('Issue date is required')
      .max(new Date(), 'Issue date cannot be in the future'),
    expiryDate: Yup.date()
      .nullable()
      .min(Yup.ref('issueDate'), 'Expiry date must be after issue date')
  }))
    .min(1, 'At least one accreditation document is required')
});

const HospitalAccreditations = ({ initialData = {}, onBack, onComplete }) => {
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [accreditations, setAccreditations] = useState(initialData.hospitalAccreditations || []);
  const [submitting, setSubmitting] = useState(false);

  if (!initialData) {
    return <p>Loading form...</p>; // Prevent rendering if data is undefined
  }

  const handleAccreditationChange = (e) => {
    const { name, value } = e.target;
    setAccreditations({ ...accreditations, [name]: value });
  };

  const handleDocumentUpload = async (index, e, setFieldValue) => {
    const file = e.target.files[0];
    if (!file) return;

    const supportedFormats = ['application/pdf'];
    if (!supportedFormats.includes(file.type)) {
      setError(`Invalid file format for ${accreditations[index].documentName}`);
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setError(`File size exceeds 5MB for ${accreditations[index].documentName}`);
      return;
    }

    setFieldValue(`hospitalAccreditations[${index}].file`, file);
    setError('');
    setSuccess('Document uploaded successfully');
  };

  const handleAccreditationSubmit = async (accreditations) => {
    setSubmitting(true); // Set submitting to true at the start
    try {
      const formData = new FormData();
      accreditations.forEach((accreditation) => {
        formData.append('hospitalAccreditations[]', JSON.stringify(accreditation));
        if (accreditation.file) {
          formData.append('files[]', accreditation.file);
        }
      });

      const response = await axios.post('http://127.0.0.1:8000/hospital/accreditation', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.status === 201) {
        setSuccess('Accreditation documents submitted successfully!');
        setError(''); // Reset error on successful submission
        setAccreditations([]); // Clear the accreditation fields
      } else {
        throw new Error('Accreditation submission failed. Please try again.');
      }
    } catch (error) {
      setError(error?.response?.data?.detail || 'Failed to submit accreditation documents');
    } finally {
      setSubmitting(false); // Always set submitting to false at the end
    }
  };

  const handleAccreditationRemove = (index) => {
    setAccreditations(accreditations.filter((_, i) => i !== index));
  };

  const handleSubmit = async (values, formikHelpers) => {
    formikHelpers.setSubmitting(true);
    try {
      await handleAccreditationSubmit(values.hospitalAccreditations);
    } finally {
      formikHelpers.setSubmitting(false);
    }
  };

  return (
    <motion.div
      className={css(styles.container)}
      initial={{ opacity: 0, y: 50 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <h2 className={css(styles.title)}>Hospital Accreditations</h2>
      {error && <div className={css(styles.error)}>{error}</div>}
      {success && <div className={css(styles.success)}>{success}</div>}
      <Formik
        initialValues={{
          hospitalAccreditations: initialData.hospitalAccreditations || [
            {
              file: null,
              documentName: '',
              issueDate: '',
              expiryDate: null
            }
          ]
        }}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ errors, touched, setFieldValue, handleSubmit, isValid }) => (
          <Form className={css(styles.form)}>
            <div className={css(styles.fieldGroup)}>
              <label className={css(styles.label)}>Accreditation and Licenses</label>
              {accreditations.map((accreditation, index) => (
                <div key={index} className={css(styles.accreditationContainer)}>
                  <button type='button' onClick={() => handleAccreditationRemove(index)} className={css(styles.removeAccreditationBtn)}>
                    X
                                    </button>
                  <div className={css(styles.accreditationFields)}>
                    <div className={css(styles.fileUploadContainer)}>
                        <input
                            type='file'
                            id={`file-${index}`}
                            onChange={(e) => handleDocumentUpload(index, e, setFieldValue)}
                          />
                        <label htmlFor={`file-${index}`} className={css(styles.uploadLabel)}>
                            {accreditation.file ? accreditation.file.name : 'Click or drag file to upload'}
                          </label>
                      </div>
                  </div>
                  <div className={css(styles.fieldGroup)}>
                    <label htmlFor={`documentName-${index}`} className={css(styles.label)}>
                        Document Name
                                        </label>
                    <Field
                        name={`hospitalAccreditations[${index}].documentName`}
                        type='text'
                        className={css(styles.input)}
                        onChange={handleAccreditationChange}
                        required
                      />
                    {errors.hospitalAccreditations && errors.hospitalAccreditations[index]?.documentName && touched.hospitalAccreditations && touched.hospitalAccreditations[index]?.documentName && (
                        <div className={css(styles.error)}>{errors.hospitalAccreditations[index].documentName}</div>
                      )}
                  </div>
                  <div className={css(styles.fieldGroup)}>
                    <label htmlFor={`issueDate-${index}`} className={css(styles.label)}>
                        Issue Date
                                        </label>
                    <Field
                        name={`hospitalAccreditations[${index}].issueDate`}
                        type='text'
                        className={css(styles.input)}
                        onChange={handleAccreditationChange}
                        required
                      />
                    {errors.hospitalAccreditations && errors.hospitalAccreditations[index]?.issueDate && touched.hospitalAccreditations && touched.hospitalAccreditations[index]?.issueDate && (
                        <div className={css(styles.error)}>{errors.hospitalAccreditations[index].issueDate}</div>
                      )}
                  </div>
                  <div className={css(styles.fieldGroup)}>
                    <label htmlFor={`expiryDate-${index}`} className={css(styles.label)}>
                        Expiry Date
                                        </label>
                    <Field
                        name={`hospitalAccreditations[${index}].expiryDate`}
                        type='text'
                        className={css(styles.input)}
                        onChange={handleAccreditationChange}
                        required
                      />
                    {errors.hospitalAccreditations && errors.hospitalAccreditations[index]?.expiryDate && touched.hospitalAccreditations && touched.hospitalAccreditations[index]?.expiryDate && (
                        <div className={css(styles.error)}>{errors.hospitalAccreditations[index].expiryDate}</div>
                      )}
                  </div>
                </div>
              ))}
              <div className={css(styles.buttonContainer)}>
                <motion.button
                  type='submit'
                  className={css(styles.button, styles.nextButton)}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={handleSubmit}
                >
                  Save
                </motion.button>
                <motion.button
                  type='button'
                  className={css(styles.button, styles.nextButton)}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => {
                    if (!isValid) {
                      console.log('Form is invalid, staying on the same page.');
                      alert('Please fill in all required fields before proceeding.');
                      return;
                    }
                    console.log('Form is valid, navigating to the next section.');
                    onComplete(); // Ensure this function correctly updates the state for the next section
                  }}
                >
                  Next
                </motion.button>
                <motion.button
                  type='button'
                  className={css(styles.button, styles.backButton)}
                  onClick={onBack}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  Back
                </motion.button>
              </div>
            </div>
          </Form>
        )}
      </Formik>
    </motion.div>
  );
};

const styles = StyleSheet.create({
  container: {
    marginBottom: '2rem'
  },
  title: {
    fontSize: '1.5rem',
    marginBottom: '1rem',
    color: '#2783F7'
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem'
  },
  fieldGroup: {
    display: 'flex',
    flexDirection: 'column',
    color: 'black'
  },
  label: {
    fontSize: '1rem',
    fontWeight: '100',
    marginBottom: '1rem',
    color: '#4a5568'
  },
  input: {
    padding: '0.5rem',
    fontSize: '1rem',
    borderRadius: '5px',
    border: '1px solid #e2e8f0'
  },
  fileInput: {
    padding: '0.5rem',
    fontSize: '1rem',
    borderRadius: '2px',
    border: '1px solid #e2e8f0'
  },
  success: {
    color: 'green',
    marginBottom: '1rem'
  },
  fileUploadContainer: {
    border: '1px dashed rgb(66, 86, 108)',
    borderRadius: '4px',
    borderColor: '#2783F7',
    background: '#f0f7ff',
    textAlign: 'center',
    cursor: 'pointer',
    color: 'rgb(51, 66, 82)',
    transition: 'all 0.3s ease',
    ':hover': {
      borderColor: '#2783F7',
      background: '#e0f2fe'
    }
  },
  error: {
    color: 'red',
    fontSize: '0.875rem',
    marginTop: '0.25rem'
  },
  buttonContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem',
    marginTop: '1rem'
  },
  button: {
    border: 'none',
    padding: '0.5rem 1rem',
    fontSize: '1rem',
    borderRadius: '5px',
    cursor: 'pointer'
  },
  nextButton: {
    backgroundColor: '#2783F7',
    color: 'white'
  },
  backButton: {
    backgroundColor: '#e2e8f0',
    color: '#4a5568'
  },
  accreditationContainer: {
    background: '#f7fafc',
    border: '1px solid #e2e8f0',
    borderRadius: '8px',
    padding: '1.5rem',
    marginBottom: '1rem',
    boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
    position: 'relative',
    transition: 'all 0.3s ease',
    ':hover': {
      boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
    }
  },
  accreditationFields: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem'
  },
  removeAccreditationBtn: {
    position: 'absolute',
    top: '1rem',
    right: '1rem',
    border: 'none',
    borderRadius: '50%',
    width: '30px',
    height: '30px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    ':hover': {
      background: '#fecaca',
      transform: 'scale(1.1)'
    }
  },
  accreditationInput: {
    width: '100%',
    padding: '0.75rem',
    borderRadius: '6px',
    border: '1px solid #e2e8f0',
    transition: 'all 0.3s ease',
    ':focus': {
      borderColor: '#2783F7',
      boxShadow: '0 0 0 3px rgba(39,131,247,0.1)'
    }
  }
});

export default HospitalAccreditations;
